import _ from 'lodash';
import * as React from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import Fuse from 'fuse.js';

import * as api from '../../../api';
import * as logger from '../../../logger';

import styles from './index.module.css';
import defaultStyles from '../../../styles/formSetStyles.module.css';

import SearchIcon from '@material-ui/icons/Search';

import ContextualHelpModal from '../../../components/ContextualHelpModal';
import InfoLabel from '../../../components/ContextualHelpModal/InfoLabel';

import { Suggestion } from '../../../components/AutocompleteAsyncInput';
import { Button } from '../../../components/Button';
import { ItemsList, Size } from '../../../components/ItemsPage/ItemsList';
import { ItemsPage } from '../../../components/ItemsPage/ItemsPage';
import { GroupInputs } from '../../../components/GroupInputs';
import {
  LoadingSpinner,
  LoadingSpinnerSize,
} from '../../../components/LoadingSpinner';
import ModalBody, { AllergyToAdd, SelectedAllergy } from './ModalBody';
import { ModalInput } from '../../../components/ModalInput';

export const SEARCH_PLACEHOLDER = 'Search by name...';
const HELP_LABEL = 'Allergy not appearing in search?';
const DEFAULT_MODAL_HELP_MARKDOWN = `## ${HELP_LABEL}

Please talk with your clinician about other allergies you may have.`;
const EMPTY_STRING = '';

///////////////////////////////////////////////////
////// TYPES

interface Props extends WithTranslation {
  navigateToNextPage: () => Promise<void>;
  setFormField: (formFieldId: string, value: string) => void;
  field: api.PrevisitFormField.PrevisitFormFieldWithConfig;
  practicePreference: api.Previsit.PracticePreference;
}

interface State {
  initialAllergiesCount?: number;
  allergies: api.Allergy.PrevisitAllergy[];
  isModalSearchOpen: boolean;
  isModalHelpOpen: boolean;
  isLoading: boolean;
  selectedAllergy: SelectedAllergy | null;
  currentText: string;
}

///////////////////////////////////////////////////
////// HELPERS

const sortAllergies = (allergies: api.Allergy.PrevisitAllergy[]) => {
  return _.sortBy(allergies, 'canRemove', 'name');
};

export const splitAndUpperFirst = (str: string): string => {
  if (!str) return '';

  return _.startCase(str.replace(/_/g, ' ').toLowerCase());
};

///////////////////////////////////////////////////
////// COMPONENT

class AllergiesPage extends React.Component<Props, State> {
  private fuse: Fuse<SelectedAllergy>;

  constructor(props: Props) {
    super(props);

    this.fuse = new Fuse(this.getTranslatedAllergyList(), {
      keys: ['displayName'],
    });

    const value = props.field.value;

    if (!value) {
      this.state = {
        allergies: [],
        initialAllergiesCount: 0,
        isModalSearchOpen: false,
        isModalHelpOpen: false,
        isLoading: false,
        selectedAllergy: null,
        currentText: EMPTY_STRING,
      };
      return;
    }

    const parsed = sortAllergies(JSON.parse(value) || []);

    this.state = {
      allergies: parsed,
      initialAllergiesCount: parsed.length,
      isModalSearchOpen: false,
      isModalHelpOpen: false,
      isLoading: false,
      selectedAllergy: null,
      currentText: EMPTY_STRING,
    };
  }

  getTranslatedAllergyList = (): SelectedAllergy[] => {
    const { practicePreference, t } = this.props;
    return practicePreference.allergyNames.map((a) => {
      const allergyData = api.Allergy.AllergyNameToStandardAllergyMap[a];
      return {
        id: allergyData.name,
        ...allergyData,
        displayName: t(allergyData.displayName),
      };
    });
  };

  setIsModalSearchOpen = (newState: boolean) => {
    this.setState({ isModalSearchOpen: newState });
  };

  setIsModalHelpOpen = (newState: boolean) => {
    this.setState({ isModalHelpOpen: newState });
  };

  navigateToNextPage = async (event: React.FormEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    event.preventDefault();
    await this.props.navigateToNextPage();
  };

  deleteAllergy = async (id: string) => {
    const { allergies } = this.state;

    if (!allergies.length) return;

    const filteredAllergies = allergies.filter((allergy) => allergy.id !== id);

    this.setState({
      allergies: filteredAllergies,
    });
    await this.updateFormField(
      filteredAllergies.length ? filteredAllergies : null,
    );
  };

  formatAllergies = (allergies: api.Allergy.PrevisitAllergy[]) => {
    const { t } = this.props;
    return allergies.map((allergy) => {
      const primaryLabel = t(
        splitAndUpperFirst(
          allergy.name === 'OTHER'
            ? allergy.nameRaw?.toUpperCase() || ''
            : allergy.name,
        ),
      );

      const reaction = (allergy.reactions && allergy.reactions[0]) || '';
      const reactionRaw =
        (allergy.reactionsRaw && allergy.reactionsRaw[0]) || '';

      const secondaryLabel = [
        (allergy.reactions && allergy.reactions[0]) === 'OTHER'
          ? reactionRaw
          : reaction,
        allergy.severity,
      ]
        .filter((x) => !!x)
        .map((elm) => t(splitAndUpperFirst(elm || '')))
        .join(', ');

      return {
        ...allergy,
        primaryLabel,
        secondaryLabel,
        canRemove: allergy.canRemove,
      };
    });
  };

  updateFormField = async (allergies: api.Allergy.PrevisitAllergy[] | null) => {
    const { field, setFormField } = this.props;
    const { id, previsitFormSetId } = field;

    const newValue = allergies ? JSON.stringify(allergies) : '';
    setFormField(id, newValue);
    try {
      await api.client.Previsit.updateFormField(
        previsitFormSetId,
        id,
        newValue,
      );
    } catch (err) {
      logger.error(err, 'Failed to update form field');
    }
  };

  addAllergy = async (allergyToAdd: AllergyToAdd) => {
    this.setIsModalSearchOpen(false);
    const { allergies } = this.state;
    const { selectedAllergy, selectedReaction, selectedSeverity } =
      allergyToAdd;

    if (!selectedAllergy || !selectedReaction || !selectedSeverity) return;

    const newAllergy: api.Allergy.PrevisitAllergy = {
      ...selectedAllergy,
      reactions: [selectedReaction],
      severity: selectedSeverity,
      canRemove: true,
    };

    const filteredAllergies = allergies.filter(
      ({ name }) => name !== newAllergy.name,
    );

    const newAllergies = [...filteredAllergies, newAllergy];

    this.setState({
      allergies: newAllergies,
      selectedAllergy: null,
      currentText: EMPTY_STRING,
    });
    await this.updateFormField(newAllergies);
  };

  handleSelect = (suggestion: Suggestion<SelectedAllergy>) => {
    const { data } = suggestion;

    if (!data) return;

    this.setState({ selectedAllergy: data });
  };

  updateFormFieldAndNavigate = async (
    formFieldValue: api.Allergy.PrevisitAllergy[] | '',
  ) => {
    this.setState({ isLoading: true });
    await this.updateFormField(formFieldValue || null);
    this.setState({ isLoading: false });
    this.props.navigateToNextPage();
  };

  getAllergySuggestions = async (
    input: string,
  ): Promise<Suggestion<SelectedAllergy>[]> => {
    const cleanedInput = input.trim().toLowerCase();
    if (!cleanedInput) return [];
    return this.fuse.search(cleanedInput).map((result) => {
      const allergy = result.item;
      return {
        primaryLabel: allergy.displayName,
        data: allergy,
      };
    });
  };

  renderModalBody = () => {
    const { selectedAllergy } = this.state;
    const { practicePreference } = this.props;

    return (
      <ModalBody
        selectedAllergy={selectedAllergy}
        severityOptions={practicePreference.allergySeverityOptions}
        addAllergy={this.addAllergy}
        handleSelect={this.handleSelect}
        getAllergySuggestions={this.getAllergySuggestions}
        initialText={this.state.currentText}
      />
    );
  };

  render() {
    const { practicePreference, t } = this.props;
    const {
      allergies,
      initialAllergiesCount,
      isModalSearchOpen,
      isModalHelpOpen,
      isLoading,
    } = this.state;
    if (!allergies) {
      return (
        <div className={styles.LoadingWrapper}>
          <h2>{t('Loading allergies')}</h2>
          <div className={styles.LoadingSpinnerWrapper}>
            <LoadingSpinner size={LoadingSpinnerSize.Small} />
          </div>
        </div>
      );
    }

    return (
      <div>
        <ItemsPage
          label={
            initialAllergiesCount
              ? 'Review and update your current allergies.'
              : 'Tell us about your current allergies.'
          }
          subLabel={
            initialAllergiesCount
              ? allergies.some((allergy) => !allergy.canRemove)
                ? 'If any of these allergies have changed please discuss this with your caregiver in person.'
                : 'Be sure to include any allergies that are new.'
              : 'Be sure to include drug and non-drug allergies.'
          }
        >
          <div className={styles.ItemsLists}>
            <ItemsList
              compact={false}
              items={this.formatAllergies(allergies)}
              primaryLabelSize={Size.Small}
              secondaryLabelSize={Size.Small}
              deleteItem={this.deleteAllergy}
              className={styles.ItemsList}
            />
          </div>
          <GroupInputs
            title={t('Add an allergy')}
            classNames={[styles.GroupInputs]}
          >
            <ModalInput
              children={this.renderModalBody()}
              isOpen={isModalSearchOpen}
              setIsOpen={(newState) => this.setIsModalSearchOpen(newState)}
              setCurrentText={(currentText) => this.setState({ currentText })}
              placeholder={t(SEARCH_PLACEHOLDER)}
              startIcon={<SearchIcon className={styles.SearchIcon} />}
              initialValue={EMPTY_STRING}
              onModalClose={() => {
                this.setState({
                  isModalSearchOpen: false,
                  selectedAllergy: null,
                  currentText: EMPTY_STRING,
                });
              }}
            />
          </GroupInputs>
        </ItemsPage>
        {practicePreference.allergyExplicitNoneEnabled ? (
          <>
            <ContextualHelpModal
              headerText={t('More information')}
              helpText={t(
                this.props.field.helpDialog || DEFAULT_MODAL_HELP_MARKDOWN,
              )}
              isOpen={isModalHelpOpen}
              onRequestClose={() => this.setIsModalHelpOpen(false)}
              onHandleHelpful={async () => this.setIsModalHelpOpen(false)}
            />
            <InfoLabel
              className={styles.InfoLabel}
              onClick={() => this.setIsModalHelpOpen(true)}
              label={t(HELP_LABEL)}
            />
          </>
        ) : (
          <></>
        )}
        <div className={defaultStyles.ButtonSubmitWrapper}>
          {allergies.length ? (
            <Button
              type="button"
              children={t('Done adding allergies')}
              disabled={isLoading}
              onClick={() => {
                this.setState({ isLoading: true });
                this.props.navigateToNextPage();
                this.setState({ isLoading: false });
              }}
            />
          ) : practicePreference.allergyExplicitNoneEnabled ? (
            <div>
              <Button
                type="button"
                children={t(`I don't have any allergies`)}
                disabled={isLoading}
                onClick={() => this.updateFormFieldAndNavigate([])}
              />
              <Button
                type="button"
                children={t('Continue')}
                disabled={isLoading}
                onClick={() => this.updateFormFieldAndNavigate('')}
              />
            </div>
          ) : (
            <div>
              <Button
                type="button"
                children={t(`I don't have any allergies`)}
                disabled={isLoading}
                onClick={this.navigateToNextPage}
              />
            </div>
          )}
        </div>
      </div>
    );
  }
}

export default withTranslation()(AllergiesPage);
